import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { ApiError } from '@/services/api'
import {
  type AssistantMergeRequest,
  resolutionAssistantApi,
} from '@/services/api/entity-resolution-assistant'
import type { MergeExactJob } from '@/services/api/entity-resolution-groups'

export const resolutionAssistantJobKey = (caseId: string, snapshotId: string) =>
  ['entity-resolution-assistant-job', caseId, snapshotId] as const

export function resolutionJobExpired(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}

/** An explicit fresh review permits retrying an unchanged queue after a terminal failure. */
export function clearFinishedResolutionJob(
  client: QueryClient,
  caseId: string,
  snapshotId: string
) {
  const key = resolutionAssistantJobKey(caseId, snapshotId)
  const job = client.getQueryData<MergeExactJob>(key)
  const expired = resolutionJobExpired(client.getQueryState(key)?.error)
  if (job && (job.status !== 'running' || expired))
    client.removeQueries({ queryKey: key, exact: true })
}

/** Cache the accepted job by snapshot so closing/reopening the sidebar resumes progress. */
export function useResolutionAssistant(caseId: string, snapshotId: string) {
  const client = useQueryClient()
  const key = resolutionAssistantJobKey(caseId, snapshotId)
  const cached = client.getQueryData<MergeExactJob>(key)
  const job = useQuery({
    queryKey: key,
    enabled: Boolean(cached?.job_id),
    queryFn: () =>
      resolutionAssistantApi.status(caseId, client.getQueryData<MergeExactJob>(key)?.job_id ?? ''),
    refetchInterval: query =>
      query.state.data?.status === 'running' && !resolutionJobExpired(query.state.error)
        ? 1500
        : false,
    staleTime: 1000,
    retry: 2,
  })
  const merge = useMutation({
    mutationFn: (body: AssistantMergeRequest) => resolutionAssistantApi.merge(caseId, body),
    onSuccess: data => client.setQueryData(key, data),
  })
  useResolutionInvalidation(caseId, job.data)
  return { merge, job, accepted: Boolean(job.data) || merge.isPending }
}

export function useResolutionInvalidation(caseId: string, job: MergeExactJob | undefined) {
  const client = useQueryClient()
  const lastInvalidation = useRef(0)
  const updatedAt = job?.updated_at
  const jobStatus = job?.status
  useEffect(() => {
    if (!updatedAt || (jobStatus === 'running' && Date.now() - lastInvalidation.current < 6000))
      return
    lastInvalidation.current = Date.now()
    void client.invalidateQueries({ queryKey: ['case', caseId, 'entity-resolution'] })
    void client.invalidateQueries({ queryKey: ['case', caseId, 'graph'] })
  }, [caseId, client, updatedAt, jobStatus])
}
